// Copyright The OpenTelemetry Authors
// SPDX-License-Identifier: Apache-2.0

import { v4 } from 'uuid';
import { SessionMetrics } from '../utils/telemetry/SessionMetrics';

interface ISession {
  userId: string;
  currencyCode: string;
  geography: string;
  device: string;
}

const sessionKey = 'session';
const defaultSession = {
  userId: v4(),
  currencyCode: 'USD',
  geography: 'Not Set - Default',
  device: 'Not Set - Default'
};

// Cache URLSearchParams to avoid creating new objects on every call
let cachedUrlParams: URLSearchParams | null = null;
let lastUrl = '';

// Track if we've already recorded a session creation metric to prevent double-counting
let sessionMetricTracked = false;

const SessionGateway = () => ({
  getSession(): ISession {
    if (typeof window === 'undefined') {
      return defaultSession;
    }

    // Get existing session from sessionStorage
    const existingSession = sessionStorage.getItem(sessionKey);
    let session = existingSession ? JSON.parse(existingSession) : { ...defaultSession };

    // Check for URL parameters and update session if present
    // Cache URLSearchParams to avoid memory leaks
    const currentUrl = window.location.search;
    if (currentUrl !== lastUrl) {
      cachedUrlParams = new URLSearchParams(currentUrl);
      lastUrl = currentUrl;
    }

    let sessionUpdated = false;

    if (cachedUrlParams?.has("device")) {
      const deviceValue = cachedUrlParams.get("device") || "Not Set - URL Params";
      if (session.device !== deviceValue) {
        session.device = deviceValue;
        sessionUpdated = true;
      }
    }

    if (cachedUrlParams?.has("geography")) {
      const geographyValue = cachedUrlParams.get("geography") || "Not Set - URL Params";
      if (session.geography !== geographyValue) {
        session.geography = geographyValue;
        sessionUpdated = true;
      }
    }

    // Save updated session to sessionStorage if it was modified
    if (sessionUpdated || !existingSession) {
      sessionStorage.setItem(sessionKey, JSON.stringify(session));

      // Track session creation metric only for new sessions (and only once)
      if (!existingSession && !sessionMetricTracked) {
        SessionMetrics.incrementSessionCreated({
          geography: session.geography,
          device: session.device
        }).catch((error: unknown) => {
          console.error('Failed to record session metric:', error);
        });
        sessionMetricTracked = true;
      }
    }

    return session as ISession;
  },
  setSessionValue<K extends keyof ISession>(key: K, value: ISession[K]) {
    const session = this.getSession();

    sessionStorage.setItem(sessionKey, JSON.stringify({ ...session, [key]: value }));
  },
});

export default SessionGateway();
